import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Activity, RefreshCw, Plus } from "lucide-react";
import { api } from "../lib/api";
import { ModuleHeader } from "../components/intel/IntelKit";
import Spinner from "../components/Spinner";
import ChainReactionModal from "../components/ChainReactionModal";
import CreateTaskModal from "../components/CreateTaskModal";
import ExecutionPlan from "./ExecutionPlan";
import BusinessPerformancePanel from "./BusinessPerformancePanel";
import StrategicRoadmapPanel from "./StrategicRoadmapPanel";

const ALIGNMENT_STYLE = {
  aligned:            { dot: "bg-status-onTrack",  text: "text-status-onTrack-text",  label: "On strategy" },
  no_target:          { dot: "bg-status-noData",   text: "text-ink-500",              label: "No target set" },
  no_strategy:        { dot: "bg-status-noData",   text: "text-ink-500",              label: "No strategy deployed" },
  revision_required:  { dot: "bg-status-drifting", text: "text-status-drifting-text", label: "Revision required" },
};

const TABS = [
  { id: "analytical", label: "Business Performance", sub: "Are we on track?" },
  { id: "vision",     label: "Strategic Roadmap",     sub: "What must we accomplish?" },
  { id: "action",     label: "Execution",             sub: "What do I need to do now?" },
];

function StrategicHeaderBand({ header }) {
  if (!header) return null;
  const t = header.target;
  const al = ALIGNMENT_STYLE[header.alignment_status] || ALIGNMENT_STYLE.no_target;
  return (
    <div className="rounded-2xl border border-ink-200 bg-white p-4 shadow-card" data-testid="cc-strategic-header">
      <div className="flex flex-wrap items-center gap-x-10 gap-y-3">
        <div className="flex flex-col" data-testid="header-target">
          <span className="text-[11px] uppercase tracking-wider text-ink-400">Strategic Target</span>
          <span className="font-display text-[15px] font-bold tabular-nums text-blue-800">
            {t ? `₹${t.target_value}Cr in ${t.horizon_years}y` : "Not set"}
          </span>
        </div>
        <div className="flex flex-col" data-testid="header-strategy-version">
          <span className="text-[11px] uppercase tracking-wider text-ink-400">Active Strategy</span>
          <span className="font-display text-[15px] font-bold text-blue-800">
            {header.strategy_version ? `Version ${header.strategy_version}` : "None deployed"}
          </span>
        </div>
        <div className="flex flex-col" data-testid="header-alignment">
          <span className="text-[11px] uppercase tracking-wider text-ink-400">Alignment</span>
          <span className={`inline-flex items-center gap-1.5 text-[13px] font-semibold ${al.text}`}>
            <span className={`w-2 h-2 rounded-full ${al.dot}`} /> {al.label}
          </span>
        </div>
        <div className="flex flex-col ml-auto min-w-[170px]" data-testid="header-progress">
          <span className="text-[11px] uppercase tracking-wider text-ink-400">Overall Progress</span>
          <div className="flex items-center gap-2 mt-0.5">
            <div className="h-2 flex-1 rounded-full bg-ink-100 overflow-hidden">
              <div className="h-full rounded-full bg-status-onTrack transition-all duration-700"
                   style={{ width: `${Math.min(100, Math.max(0, header.progress_pct || 0))}%` }} />
            </div>
            <span className="text-[12px] font-bold tabular-nums text-status-onTrack-text">{header.progress_pct ?? 0}%</span>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function CommandCenter() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true); // true only until the FIRST successful load
  const [refreshing, setRefreshing] = useState(false);
  const [payload, setPayload] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [error, setError] = useState("");        // hard error — nothing has ever loaded
  const [refreshError, setRefreshError] = useState(false); // soft error — keep showing last good data
  const [chain, setChain] = useState(null);
  const [tab, setTab] = useState("action");
  const [showCreateTask, setShowCreateTask] = useState(false);
  const loadedOnce = useRef(false);

  const load = useCallback(async () => {
    if (loadedOnce.current) setRefreshing(true); else setLoading(true);
    // PERF (2026-08 latency audit): the LLM narrative (~3s) blocks first
    // paint for a screen whose DEFAULT tab (Execution) never shows it —
    // `system_briefing` only appears on the Business Performance tab. Load
    // core data fast (use_llm: false), then fetch just the briefing text
    // in the background (no re-fetch of metrics/decisions) and patch it in
    // once ready — the founder never waits for it.
    const res = await api.commandCenter({ useLlm: false });
    if (res.ok) {
      loadedOnce.current = true;
      setPayload(res.data);
      setError("");
      setRefreshError(false);
      if (res.data.data_available && res.data.pulse) {
        api.commandCenterBriefing({ pulse: res.data.pulse, decisions: res.data.decisions }).then((briefingRes) => {
          if (briefingRes.ok) {
            setPayload((p) => (p ? { ...p, system_briefing: briefingRes.data.system_briefing } : p));
          }
        });
      }
    } else if (loadedOnce.current) {
      // Phase 6, section 12/13 — never blank a working Command Center on a
      // transient refresh failure; keep the last good payload visible.
      setRefreshError(true);
    } else {
      setError(res.error || "Could not load Command Center.");
    }
    setLoading(false);
    setRefreshing(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const onAct = async (d, action) => {
    const ruleId = d.rule_id || d.source_rule_id;
    setBusyId(d.id);
    const res = await api.commandCenterActChain({
      action, ruleId, decisionId: d.id, title: d.title, role: d.owner_seat,
    });
    setBusyId(null);
    if (res.ok && res.data) {
      // Replace the whole payload with the server-recomputed Business-OS state.
      setPayload((p) => ({ ...p, ...res.data }));
      if (action === "act" && res.data.chain_reaction) {
        setChain(res.data.chain_reaction);
      }
    }
  };

  const openNext = (next) => {
    setChain(null);
    if (next?.module_path) navigate(next.module_path);
  };

  const execPlan = payload?.execution_plan;

  return (
    <div className="space-y-6 mobile:px-4 mobile:py-4 tablet:px-5 tablet:py-4" data-testid="command-center-page">
      <ModuleHeader
        overline="Business Operating System"
        title="Command Center"
        right={
          <div className="flex items-center gap-3">
            <button onClick={() => setShowCreateTask(true)} className="inline-flex items-center gap-1 hover:text-blue-800" data-testid="command-center-create-task">
              <Plus className="w-3.5 h-3.5" /> Create Task
            </button>
            <button onClick={load} className="inline-flex items-center gap-1 hover:text-blue-800" data-testid="command-center-refresh">
              <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? "animate-spin" : ""}`} /> Refresh
            </button>
          </div>
        }
        testid="command-center-header"
      />

      {loading && (
        <div className="flex items-center justify-center py-20"><Spinner /></div>
      )}

      {!loading && error && (
        <div className="rounded-xl border border-status-critical-border bg-status-critical-bg p-4 text-[13px] text-status-critical-text" data-testid="command-center-error">
          <p>{error}</p>
          <button onClick={load} className="mt-2 inline-flex items-center gap-1.5 rounded-md border border-status-critical-border bg-white px-3 py-2 text-[12px] font-semibold hover:bg-status-critical-bg" data-testid="command-center-retry">Retry</button>
        </div>
      )}

      {!loading && !error && payload && !payload.data_available && (
        <div className="rounded-xl border border-ink-200 bg-white p-8 text-center" data-testid="command-center-empty">
          <Activity className="w-8 h-8 text-ink-300 mx-auto" />
          <p className="mt-3 text-[15px] font-display font-semibold text-blue-800">No decisions yet</p>
          <p className="mt-1 text-[13px] text-ink-500">Upload a P&amp;L or Balance Sheet in Financial Sync to activate cross-functional intelligence.</p>
        </div>
      )}

      {!loading && payload?.data_available && (
        <>
          {refreshError && (
            <div data-testid="command-center-refresh-error" className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-[12px] text-amber-700">
              <span>Unable to refresh Command Center. Showing the last available data.</span>
              <button onClick={load} className="font-semibold underline">Retry</button>
            </div>
          )}
          <StrategicHeaderBand header={payload.strategic_header} />

          <div className="flex items-center gap-1 rounded-lg border border-ink-200 bg-white p-1 w-fit flex-wrap" data-testid="cc-tabs">
            {TABS.map((t) => (
              <button key={t.id} onClick={() => setTab(t.id)} data-testid={`cc-tab-${t.id}`}
                className={`text-left px-3.5 py-2 rounded-md transition-colors ${tab === t.id ? "bg-blue-800 text-white" : "text-ink-600 hover:bg-ink-50"}`}>
                <div className="text-[12px] font-semibold">{t.label}</div>
                <div className={`text-[10.5px] ${tab === t.id ? "text-blue-200" : "text-ink-400"}`}>{t.sub}</div>
              </button>
            ))}
          </div>

          {tab === "analytical" && (
            <BusinessPerformancePanel analytical={payload.analytical} systemBriefing={payload.system_briefing} />
          )}

          {tab === "vision" && (
            <StrategicRoadmapPanel vision={payload.vision} />
          )}

          {tab === "action" && (
            execPlan
              ? <ExecutionPlan plan={execPlan} onAct={onAct} busyId={busyId} />
              : <div className="rounded-xl border border-ink-200 bg-white p-6 text-[13px] text-ink-500" data-testid="execution-unavailable">Execution plan unavailable.</div>
          )}
        </>
      )}

      {chain && (
        <ChainReactionModal
          chain={chain}
          onClose={() => setChain(null)}
          onOpenNext={openNext}
        />
      )}
      <CreateTaskModal open={showCreateTask} onClose={() => setShowCreateTask(false)} onCreated={load} />
    </div>
  );
}
